<template lang="html">
  <div class="category-hub flex-1 bg-elevated">
    <div class="mx-auto w-full max-w-(--ui-container) px-4 py-5 sm:px-6 lg:py-6">
      <CategoryBreadcrumbs
        class="mb-3"
        :node="node"
        :by-ids="byIds"
      />

      <div class="mb-5">
        <h1 class="text-2xl font-bold tracking-tight text-highlighted sm:text-3xl">
          {{ node.name }}
        </h1>
        <p
          v-if="children.length"
          class="mt-1 text-sm text-muted"
        >
          Выберите подкатегорию
        </p>
      </div>

      <div
        v-if="children.length"
        class="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 lg:grid-cols-3 xl:grid-cols-4"
      >
        <CategoryCard
          v-for="(child, index) in children"
          :key="child.id"
          size="hub"
          :category="child"
          :eager="index < 5"
        />
      </div>

      <div
        v-else
        class="flex flex-col items-center justify-center gap-3 py-16 text-muted"
      >
        <UIcon
          name="i-lucide-layout-grid"
          class="size-10 opacity-50"
        />
        <p class="text-sm">
          Подкатегории пока недоступны
        </p>
        <NuxtLink
          :to="fallbackRoute"
          class="text-sm text-highlighted underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
        >
          {{ fallbackLabel }}
        </NuxtLink>
      </div>

      <CategoryPopularLinks
        class="mt-8"
        :links="popularLinks"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Category hub composition (Phase 6 / R2 / S9–S13 / Marketplace Retail).
 * Non-leaf page: H1 + breadcrumbs + child card grid. No product wall (R6).
 * Child destinations via CategoryCard → shared resolver (hub→hub / hub→leaf).
 */
import CategoryBreadcrumbs from '#Products/components/Catalog/CategoryBreadcrumbs.vue'
import CategoryCard from '#Products/components/Catalog/CategoryCard.vue'
import CategoryPopularLinks from '#Products/components/Catalog/Hub/CategoryPopularLinks.vue'
import type { CategoryDto } from '#Products/models/Pim/CategoryDto'
import { categoryAncestorChain } from '#Products/support/categoryAncestorChain'

const props = defineProps<{
  node: CategoryDto
  byIds: Record<string, CategoryDto>
}>()

const { routing } = useProducts()
const { resolve } = useCategoryDestination()

const children = computed(() =>
  (props.node.childrenIds ?? [])
    .map(id => props.byIds[id])
    .filter((child): child is CategoryDto => child != null)
    .toSorted((a, b) => a.order - b.order),
)

/**
 * P1 popular strip (S13 / Q-H1): no PIM "popular" flag yet — omit until a
 * curated source exists. Empty → CategoryPopularLinks hides itself.
 */
const popularLinks = computed(() => [] as {
  id: number | string
  label: string
  to: string
}[])

const parentCategory = computed(() => {
  const ancestors = categoryAncestorChain(props.node, props.byIds)
  return ancestors.at(-1)
})

const fallbackRoute = computed(() =>
  parentCategory.value
    ? resolve(parentCategory.value).route
    : routing.getCatalog().route,
)

const fallbackLabel = computed(() =>
  parentCategory.value
    ? `К ${parentCategory.value.name}`
    : 'В каталог',
)
</script>
